import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";

import {
  CALORIES_PER_CARBOHYDRATE_GRAM,
  CALORIES_PER_FAT_GRAM,
  CALORIES_PER_PROTEIN_GRAM,
} from "@/config/constants";
import { MealPatternItemAttributes, MealPatternNameKind } from "@/gql/graphql";
import { mealPatternMultiplier } from "@/utils/food";
import { round } from "@/utils/number";
import { pluralize } from "@/utils/string";

import { Cell } from "./Cell";

export default class MealPatternItem {
  uuid: string;
  name: MealPatternNameKind;
  amountServings: number;

  cell: Cell;

  get energyKcal(): number {
    return this.multiplier.calories * this.amountServings;
  }

  get carbohydrateGrams(): number {
    return this.multiplier.carbohydrates * this.amountServings;
  }

  get carbohydratePercent(): number {
    return (
      (this.carbohydrateGrams * CALORIES_PER_CARBOHYDRATE_GRAM) /
      this.energyKcal
    );
  }

  get proteinGrams(): number {
    return this.multiplier.proteins * this.amountServings;
  }

  get proteinPercent(): number {
    return (this.proteinGrams * CALORIES_PER_PROTEIN_GRAM) / this.energyKcal;
  }

  get fatGrams(): number {
    return this.multiplier.fats * this.amountServings;
  }

  get fatPercent(): number {
    return (this.fatGrams * CALORIES_PER_FAT_GRAM) / this.energyKcal;
  }

  get amountWithUnit(): string {
    return `${round(this.amountServings, 2)} ${pluralize(
      "serving",
      this.amountServings
    )}`;
  }

  private get multiplier() {
    return mealPatternMultiplier(this.name);
  }

  constructor(
    cell: Cell,
    name: MealPatternNameKind,
    amountServings: number,
    uuid = uuidv4()
  ) {
    makeAutoObservable(this);
    this.cell = cell;
    this.name = name;
    this.amountServings = amountServings;
    this.uuid = uuid;
  }

  incrementAmount(): void {
    this.setAmount(this.amountServings + 1);
  }

  decrementAmount(): void {
    this.setAmount(this.amountServings - 1);
  }

  setAmount(value: number): void {
    this.amountServings = value;
    if (this.amountServings <= 0) this.remove();
  }

  remove(): void {
    this.cell.removeMealPatternItem(this);
  }

  toAttributes(): MealPatternItemAttributes {
    return {
      uuid: this.uuid,
      name: this.name,
      amountServings: this.amountServings,
    };
  }
}
